/**
 * Yalıda Davet · pricing engine (shared).
 * Weekend = Friday & Saturday nights (guest stays into Sat/Sun).
 * All amounts in kuruş (integers).
 */
import { calculateDepositKurus, getTierForGuests, checkEligibility } from './rules.js';
import type { Quote, QuoteLine, MansionAddon } from './types.js';

export const WEEKEND_WEEKDAYS = [5, 6]; // Fri=5, Sat=6 (getUTCDay)

export function isWeekendNight(dateIso: string): boolean {
  const d = new Date(`${dateIso}T00:00:00Z`);
  return WEEKEND_WEEKDAYS.includes(d.getUTCDay());
}

export function eachNight(checkin: string, checkout: string): string[] {
  const start = new Date(`${checkin}T00:00:00Z`);
  const end = new Date(`${checkout}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) throw new Error('DATE_INVALID');
  if (end <= start) throw new Error('DATE_RANGE_INVALID');
  const nights: string[] = [];
  for (let d = new Date(start); d < end; d.setUTCDate(d.getUTCDate() + 1)) {
    nights.push(d.toISOString().slice(0, 10));
  }
  return nights;
}

export function countNights(checkin: string, checkout: string) {
  const nights = eachNight(checkin, checkout);
  const weekend = nights.filter(isWeekendNight).length;
  return { nights: nights.length, weekendNights: weekend, weekdayNights: nights.length - weekend, dates: nights };
}

export interface PricingMansion {
  id: string;
  dailyPriceKurus: number;
  weekendPriceKurus: number;
  cleaningFeeKurus: number;
  serviceFeePercent: number;
  gardenSizeM2: number;
  maxCapacity: number;
  hasStagingArea: boolean;
  hasCateringArea: boolean;
  checkoutDeadline: string;
  noiseCurfewTime: string;
  isResidentialZone: boolean;
  approvedEventTypeIds: string[];
}

export interface PricingInput {
  mansion: PricingMansion;
  checkin: string;
  checkout: string;
  guestCount: number;
  eventTypeId: string;
  eventPriceMultiplier?: number;
  addons?: MansionAddon[];
  /** Per-date price overrides from mansion_availability. */
  priceOverrides?: Record<string, number>;
}

export function addonTotalKurus(addon: MansionAddon, nights: number, guestCount: number): number {
  switch (addon.pricingModel) {
    case 'per_night':
      return addon.priceKurus * nights;
    case 'per_guest':
      return addon.priceKurus * guestCount;
    default:
      return addon.priceKurus;
  }
}

export function buildQuote(input: PricingInput): Quote {
  const { mansion, checkin, checkout, guestCount, eventTypeId } = input;
  const multiplier = input.eventPriceMultiplier ?? 1;
  const { nights, weekdayNights, weekendNights, dates } = countNights(checkin, checkout);

  const eligibility = checkEligibility({
    guestCount,
    gardenSizeM2: mansion.gardenSizeM2,
    maxCapacity: mansion.maxCapacity,
    eventTypeId,
    approvedEventTypeIds: mansion.approvedEventTypeIds,
    hasStagingArea: mansion.hasStagingArea,
    hasCateringArea: mansion.hasCateringArea,
  });

  // Base amount: weekday vs weekend nightly rate, honouring per-date overrides.
  let baseAmountKurus = 0;
  for (const date of dates) {
    const override = input.priceOverrides?.[date];
    const nightly = override ?? (isWeekendNight(date) ? mansion.weekendPriceKurus : mansion.dailyPriceKurus);
    baseAmountKurus += Math.round(nightly * multiplier);
  }

  const addons = input.addons ?? [];
  const addonsAmountKurus = addons.reduce((sum, a) => sum + addonTotalKurus(a, nights, guestCount), 0);

  const cleaningFeeKurus = mansion.cleaningFeeKurus;
  const serviceFeeKurus = Math.round(((baseAmountKurus + addonsAmountKurus) * mansion.serviceFeePercent) / 100);

  // Deposit scales with the guest-count tier, based on the average nightly rate.
  const avgNightly = Math.round(baseAmountKurus / Math.max(nights, 1));
  const depositKurus = eligibility.eligible ? calculateDepositKurus(avgNightly, guestCount) : 0;

  const totalKurus = baseAmountKurus + addonsAmountKurus + cleaningFeeKurus + serviceFeeKurus + depositKurus;
  const tier = eligibility.tier ?? getTierForGuests(Math.min(guestCount, 150)).tier;

  const lines: QuoteLine[] = [];
  if (weekdayNights > 0) {
    lines.push({ label: `Hafta içi (${weekdayNights} gece)`, amountKurus: Math.round(mansion.dailyPriceKurus * multiplier) * weekdayNights, detail: 'Günlük fiyat' });
  }
  if (weekendNights > 0) {
    lines.push({ label: `Hafta sonu (${weekendNights} gece)`, amountKurus: Math.round(mansion.weekendPriceKurus * multiplier) * weekendNights, detail: 'Hafta sonu fiyatı' });
  }
  for (const a of addons) {
    lines.push({ label: a.labelTr, amountKurus: addonTotalKurus(a, nights, guestCount) });
  }
  if (cleaningFeeKurus > 0) lines.push({ label: 'Temizlik ücreti', amountKurus: cleaningFeeKurus });
  lines.push({ label: 'Hizmet bedeli', amountKurus: serviceFeeKurus, detail: `%${mansion.serviceFeePercent}` });
  lines.push({ label: `Hasar depozitosu (Tier ${tier})`, amountKurus: depositKurus, detail: 'Etkinlik sonrası iade edilir' });

  return {
    mansionId: mansion.id,
    nights,
    weekdayNights,
    weekendNights,
    guestCount,
    tier: tier as 1 | 2 | 3,
    requiredGardenM2: eligibility.requiredGardenM2 ?? 0,
    baseAmountKurus,
    addonsAmountKurus,
    cleaningFeeKurus,
    serviceFeeKurus,
    depositKurus,
    totalKurus,
    lines,
    eligible: eligibility.eligible,
    blockReason: eligibility.eligible ? null : eligibility.reason,
    blockMessageTr: eligibility.eligible ? null : eligibility.messageTr,
    checkoutDeadline: mansion.checkoutDeadline,
    noiseCurfewTime: mansion.noiseCurfewTime,
    isResidentialZone: mansion.isResidentialZone,
  };
}

/** 2500000 → "25.000 ₺" */
export function formatKurus(kurus: number, withSymbol = true): string {
  const lira = kurus / 100;
  const formatted = new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: lira % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(lira);
  return withSymbol ? `${formatted} ₺` : formatted;
}

export const toKurus = (lira: number) => Math.round(lira * 100);
export const toLira = (kurus: number) => kurus / 100;
